import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Phone, MapPin, Star, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export const HeroSection = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1487530811176-3780de880c2d?w=1920&q=80"
          alt="Bouquet de fleurs élégant"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text Content */}
          <div className="space-y-8">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/80 backdrop-blur-sm"
            >
              <Star className="w-4 h-4 text-accent fill-accent" />
              <span className="text-sm font-medium text-primary">4.9/5 · 193 avis Google</span>
            </motion.div>

            {/* Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-primary leading-tight tracking-tight"
            >
              L'art floral
              <br />
              <span className="text-accent">au cœur de Sion</span>
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl"
            >
              Depuis plus de 20 ans, Alma Fleur SA sublime vos émotions à travers
              des créations florales uniques. Bouquets, compositions et
              événements — notre savoir-faire artisanal est à votre service.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              <Link to="/contact" data-testid="hero-order-btn">
                <Button
                  size="lg"
                  className="rounded-full px-8 py-6 text-lg font-medium bg-primary hover:bg-primary/90 transition-all hover:scale-105 hover:shadow-lg gap-2"
                >
                  Commander
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:+41273232316" data-testid="hero-phone-btn">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 py-6 text-lg font-medium border-primary/20 hover:bg-primary/5 gap-2"
                >
                  <Phone className="w-5 h-5" />
                  Appeler
                </Button>
              </a>
            </motion.div>

            {/* Location Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex items-center gap-3 text-muted-foreground"
            >
              <MapPin className="w-5 h-5 text-accent" />
              <span>Av. Ritz 33, 1950 Sion · Livraison et retrait en magasin</span>
            </motion.div>
          </div>

          {/* Decorative Image Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="hidden lg:block"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-accent/20 rounded-3xl blur-3xl" />
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1561181286-d3fee7d55364?w=800&q=80"
                  alt="Composition florale artisanale"
                  className="w-full h-[500px] object-cover"
                />
                {/* Floating Card */}
                <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-6">
                  <p className="font-accent italic text-lg text-primary">
                    "Chaque fleur raconte une histoire, chaque bouquet transmet une émotion."
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">— L'équipe Alma Fleur</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="w-6 h-10 border-2 border-primary/30 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-1.5 h-3 bg-primary/50 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};
